import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Edit, Copy, Trash2, User, Calendar } from 'lucide-react';
import { postData, serverURL } from "../../services/NodeServices";

const ReviewList = ({product_id}) => {
  // Start with empty array since we'll load from API
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getAllReviews = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await postData('api/products/review/byid', {product_id: product_id});

      if (result.status && result.data && result.data.reviews) {
        console.log(result.data);
        setReviews(result.data.reviews);
      } else {
        // If no reviews found, set empty array
        setReviews([]);
      }
    } catch (err) {
      console.error('Error fetching reviews:', err);
      setError('Failed to load reviews');
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (product_id) {
      getAllReviews();
    }
  }, [product_id]);

  const [filter, setFilter] = useState('all');

  const StarRating = ({ rating }) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating 
                ? 'fill-orange-500 text-orange-500' 
                : 'text-gray-200'
            }`}
          />
        ))}
        <span className="ml-2 text-sm font-medium text-gray-600">{rating}/5</span>
      </div>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getAverageRating = () => {
    if (reviews.length === 0) return 0;
    const total = reviews.reduce((sum, review) => sum + review.rating, 0);
    return (total / reviews.length).toFixed(1);
  };

  const getRatingCounts = () => {
    const counts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach(review => {
      counts[review.rating]++;
    });
    return counts;
  };

  const filteredReviews = filter === 'all' 
    ? reviews 
    : reviews.filter(review => review.rating === parseInt(filter));

  const handleEdit = (reviewId) => {
    console.log('Edit review:', reviewId);
  };

  const handleCopy = (reviewText) => {
    navigator.clipboard.writeText(reviewText);
    console.log('Copied review text');
  };

  const handleDelete = (reviewId) => {
    setReviews(reviews.filter(review => review.reviewid !== reviewId));
  };

  const ratingCounts = getRatingCounts();

  // Loading state
  if (loading) {
    return (
      <div className="w-full mx-auto bg-white min-h-screen mt-8">
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">Loading reviews...</div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="w-full mx-auto bg-white min-h-screen mt-8">
        <div className="flex items-center justify-center py-12">
          <div className="text-red-500">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full mx-auto bg-white mt-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white border border-gray-200 rounded-lg p-6 mb-8"
      >
        <h1 className="text-xl text-left w-full text-gray-800 mb-4">Product Reviews</h1>
        
        {reviews.length > 0 ? (
          <>
            {/* Rating Summary */}
            <div className="flex items-center justify-start mb-6">
              <div className="flex items-center space-x-4">
                <div className="text-center">
                  <div className="text-4xl font-bold text-orange-500">{getAverageRating()}</div>
                  <StarRating rating={Math.round(getAverageRating())} />
                  <div className="text-sm text-gray-500 mt-1">{reviews.length} reviews</div>
                </div>
              </div>
              
              {/* Rating Distribution */}
              <div className="flex-1 max-w-md ml-8">
                {[5, 4, 3, 2, 1].map(rating => (
                  <div key={rating} className="flex items-center mb-1">
                    <span className="text-sm text-gray-600 w-3">{rating}</span>
                    <Star className="w-3 h-3 fill-orange-500 text-orange-500 mx-1" />
                    <div className="flex-1 bg-gray-100 rounded-full h-2 mx-2">
                      <div 
                        className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(ratingCounts[rating] / reviews.length) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-600 w-6">{ratingCounts[rating]}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Filter Buttons */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  filter === 'all'
                    ? 'bg-orange-500 text-white'
                    : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                All Reviews
              </button>
              {[5, 4, 3, 2, 1].map(rating => (
                <button
                  key={rating}
                  onClick={() => setFilter(rating.toString())}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    filter === rating.toString()
                      ? 'bg-orange-500 text-white'
                      : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {rating} Star{rating !== 1 ? 's' : ''} ({ratingCounts[rating]})
                </button>
              ))}
            </div>
          </>
        ) : (
          <div className="text-center py-8 text-left">
            <div className="text-gray-400 text-md text-left">No reviews available for this product yet.</div>
          </div>
        )}
      </motion.div>

      {/* Reviews List */}
      {reviews.length > 0 && (
        <AnimatePresence>
          <div className="space-y-4">
            {filteredReviews.map((review, index) => (
              <motion.div
                key={review.reviewid}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-gray-200 rounded-lg transition-shadow duration-300 p-6"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-gray-500" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800 capitalize text-left">
                        {review.username}
                      </h3>
                      <div className="flex items-center text-sm text-gray-500 space-x-2">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(review.created_at)}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center space-x-2">
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleEdit(review.reviewid)}
                      className="p-2 text-gray-400 hover:text-orange-500 hover:bg-orange-50 rounded-full transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleCopy(review.review_text)}
                      className="p-2 text-gray-400 hover:text-orange-500 hover:bg-orange-50 rounded-full transition-colors"
                    >
                      <Copy className="w-4 h-4" />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleDelete(review.reviewid)}
                      className="p-2 text-gray-400 hover:text-orange-500 hover:bg-orange-50 rounded-full transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </motion.button>
                  </div>
                </div>

                <div className="mb-3">
                  <StarRating rating={review.rating} />
                </div>

                <p className="text-gray-700 leading-relaxed mb-4 capitalize w-full text-left">
                  {review.review_text}
                </p>

                <div className="flex items-center justify-between text-xs text-gray-400">
                  <span>Review ID: {review.reviewid}</span>
                  <div className="flex space-x-4">
                    <span>Product ID: {review.product_id}</span>
                    {review.variant_id && <span>Variant ID: {review.variant_id}</span>}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </AnimatePresence>
      )}

      {filteredReviews.length === 0 && reviews.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-left py-12 w-full"
        >
          <div className="text-gray-400 text-md w-full text-left">No reviews found for the selected rating.</div>
        </motion.div>
      )}
    </div>
  );
};

export default ReviewList;